import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { IconPalette, IconStar } from '../components/Icons';

const InteriorDesignServiceView = () => {
  const { t } = useLanguage();
  
  const [searchFilters, setSearchFilters] = useState({
    style: '',
    budget: '',
  });

  const [designers] = useState([
    {
      id: 1,
      name: 'Modern Living Interiors',
      serviceArea: ['Makati', 'BGC', 'Ortigas'],
      specialtyStyles: ['Modern', 'Minimalist'],
      rating: 4.9,
      reviews: 89,
      budgetRange: '₱800,000~₱1,500,000',
      portfolio: [
        'https://images.unsplash.com/photo-1565182999561-18d7dc61c393?w=400',
        'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
      ],
    },
    {
      id: 2,
      name: 'Philippine Design Studio',
      serviceArea: ['Quezon City', 'Marikina', 'Pasig'],
      specialtyStyles: ['Scandinavian', 'Minimalist'],
      rating: 4.7,
      reviews: 67,
      budgetRange: '₱300,000~₱800,000',
      portfolio: [
        'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400',
        'https://images.unsplash.com/photo-1567225557594-88d73e55f2cb?w=400',
      ],
    },
    {
      id: 3,
      name: 'Classic Filipino Homes',
      serviceArea: ['Alabang', 'Las Piñas', 'Parañaque'],
      specialtyStyles: ['Classic', 'Modern'],
      rating: 4.8,
      reviews: 123,
      budgetRange: '₱1,500,000+',
      portfolio: [
        'https://images.unsplash.com/photo-1558211583-d26f610c1eb1?w=400',
        'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=400',
      ],
    },
  ]);

  const styles = [
    { value: '', label: t('allStyles') },
    { value: 'modern', label: t('modern') },
    { value: 'minimalist', label: t('minimalist') },
    { value: 'classic', label: t('classic') },
    { value: 'scandinavian', label: t('scandinavian') },
  ];

  const budgets = [
    { value: '', label: t('allBudgets') },
    { value: 'budget1', label: 'Under ₱300,000' },
    { value: 'budget2', label: '₱300,000-₱800,000' },
    { value: 'budget3', label: '₱800,000-₱1,500,000' },
    { value: 'budget4', label: 'Over ₱1,500,000' },
  ];

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<IconStar key={i} className="w-4 h-4" fill="currentColor" />);
    }
    if (hasHalfStar) {
      stars.push(<IconStar key="half" className="w-4 h-4" fill="currentColor" style={{ opacity: 0.5 }} />);
    }
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<IconStar key={`empty-${i}`} className="w-4 h-4" />);
    }

    return stars;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('interiorDesignService')}</h1>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('searchInteriorDesigners')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('style')}
            </label>
            <select
              value={searchFilters.style}
              onChange={(e) => setSearchFilters({ ...searchFilters, style: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {styles.map((style) => (
                <option key={style.value} value={style.value}>
                  {style.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('budgetRange')}
            </label>
            <select
              value={searchFilters.budget}
              onChange={(e) => setSearchFilters({ ...searchFilters, budget: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {budgets.map((budget) => (
                <option key={budget.value} value={budget.value}>
                  {budget.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md">
              {t('search')}
            </button>
          </div>
        </div>
      </div>

      {/* Interior Designer List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {designers.map((designer) => (
          <div key={designer.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            {/* Portfolio Image */}
            <div className="h-48 bg-gray-200 relative">
              <img
                src={designer.portfolio[0]}
                alt={`${designer.name} portfolio`}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2 bg-white rounded-full p-2">
                <IconPalette className="w-4 h-4 text-gray-600" />
              </div>
            </div>

            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{designer.name}</h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('serviceArea')}: {designer.serviceArea.join(', ')}
              </p>
              
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-1">{t('specialtyStyles')}:</p>
                <div className="flex flex-wrap gap-2">
                  {designer.specialtyStyles.map((style, index) => (
                    <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                      {style}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center mb-1">
                  <div className="flex text-yellow-400">
                    {renderStars(designer.rating)}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">
                    {designer.rating} ({designer.reviews} reviews)
                  </span>
                </div>
                <p className="text-sm text-gray-600">{t('budget')}: {designer.budgetRange}</p>
              </div>

              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md">
                {t('requestConsultation')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {designers.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">{t('noSearchResults')}</p>
        </div>
      )}
    </div>
  );
};

export default InteriorDesignServiceView;
